class CartDrawerRecommendations {
  constructor(container) {
    this.container = container;

    this.track = container.querySelector(
      '[data-recommendations-track]'
    );

    this.prevButton = container.querySelector(
      '.cart-drawer-recommendations__prev'
    );

    this.nextButton = container.querySelector(
      '.cart-drawer-recommendations__next'
    );

    this.currentIndex = 0;
    this.products = [];

    this.init();
  }

  async init() {
    try {
      this.showLoading();

      const productId =
        this.container.dataset.productId;

      /*
       * No product ID = no recommendations
       */
      if (!productId) {
        this.hide();
        return;
      }

      /*
       * Shopify Product Recommendations API
       */
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=8&intent=related`
      );

      if (!response.ok) {
        throw new Error(
          'Unable to load recommendations'
        );
      }

      const data = await response.json();

      /*
       * No recommended products
       */
      if (
        !data.products ||
        !data.products.length
      ) {
        this.hide();
        return;
      }

      this.products = data.products;

      /*
       * Render products
       */
      this.render();

      /*
       * Arrow events
       */
      if (this.prevButton) {
        this.prevButton.addEventListener(
          'click',
          () => {
            this.goTo(
              this.currentIndex - 1
            );
          }
        );
      }

      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          () => {
            this.goTo(
              this.currentIndex + 1
            );
          }
        );
      }

      /*
       * Initial arrow state
       */
      this.updateArrows();

    } catch (error) {
      console.error(
        'Cart drawer recommendations:',
        error
      );

      this.hide();
    }
  }

  /*
   * Loading state
   */
  showLoading() {
    this.container.classList.add(
      'is-loading'
    );

    if (!this.track) {
      return;
    }

    this.track.innerHTML = `
      <div class="cart-drawer-recommendations__loading">
        Loading recommendations...
      </div>
    `;
  }

  /*
   * Render recommendation products
   */
  render() {
    this.container.classList.remove(
      'is-loading'
    );

    if (!this.track) {
      return;
    }

    this.track.innerHTML = this.products
      .map((product, index) => {

        /*
         * Get first variant
         *
         * We are NOT adding this variant
         * to cart. We only use the product
         * URL below.
         */
        const variant =
          product.variants &&
          product.variants.length
            ? product.variants[0]
            : null;

        if (!variant) {
          return '';
        }

        /*
         * Product image
         */
        const image =
          product.featured_image ||
          (
            product.images &&
            product.images.length
              ? product.images[0]
              : null
          );

        /*
         * Product price
         */
        const price =
          this.formatMoney(
            variant.price
          );

        /*
         * Compare-at price
         */
        const comparePrice =
          variant.compare_at_price &&
          Number(
            variant.compare_at_price
          ) >
            Number(
              variant.price
            )
              ? this.formatMoney(
                  variant.compare_at_price
                )
              : '';

        /*
         * Shopify product URL
         */
        const productUrl =
          product.url || '#';

        return `
          <div
            class="cart-drawer-recommendation"
            data-slide="${index}"
          >

            <!-- Product Image -->
            <a
              href="${productUrl}"
              class="cart-drawer-recommendation__image"
              aria-label="${this.escapeHtml(
                product.title
              )}"
            >

              ${
                image
                  ? `
                    <img
                      src="${image}"
                      alt="${this.escapeHtml(
                        product.title
                      )}"
                      loading="lazy"
                      width="80"
                      height="80"
                    >
                  `
                  : ''
              }

            </a>

            <!-- Product Information -->
            <div
              class="cart-drawer-recommendation__info"
            >

              <!-- Product Title -->
              <a
                href="${productUrl}"
                class="cart-drawer-recommendation__title"
              >
                ${this.escapeHtml(
                  product.title
                )}
              </a>

              <!-- Product Price -->
              <div
                class="cart-drawer-recommendation__price"
              >

                <span
                  class="cart-drawer-recommendation__price--sale"
                >
                  ${price}
                </span>

                ${
                  comparePrice
                    ? `
                      <span
                        class="cart-drawer-recommendation__price--compare"
                      >
                        ${comparePrice}
                      </span>
                    `
                    : ''
                }

              </div>

            </div>

            <!--
              IMPORTANT:

              This is now an anchor link.

              Clicking "Add" DOES NOT add the
              product to cart.

              It opens the product page.
            -->
            <a
              href="${productUrl}"
              class="cart-drawer-recommendation__add"
              aria-label="View ${this.escapeHtml(
                product.title
              )}"
            >
              Add
            </a>

          </div>
        `;
      })
      .join('');

    /*
     * No bindAddButtons() required because
     * the button is now a normal <a> link.
     */
  }

  /*
   * Slider navigation
   */
  goTo(index) {

    /*
     * Don't allow index outside
     * available products
     */
    if (
      index < 0 ||
      index >= this.products.length
    ) {
      return;
    }

    this.currentIndex = index;

    /*
     * Move slider
     *
     * Assumes each product occupies
     * 100% of the slider width.
     */
    if (this.track) {
      this.track.style.transform =
        `translateX(-${
          index * 100
        }%)`;
    }

    this.updateArrows();
  }

  /*
   * Update previous/next buttons
   */
  updateArrows() {

    if (
      !this.prevButton ||
      !this.nextButton
    ) {
      return;
    }

    /*
     * Previous button
     */
    this.prevButton.disabled =
      this.currentIndex === 0;

    /*
     * Next button
     */
    this.nextButton.disabled =
      this.currentIndex >=
      this.products.length - 1;
  }

  /*
   * Hide recommendations
   */
  hide() {
    this.container.classList.add(
      'is-hidden'
    );
  }

  /*
   * Format Shopify money
   */
  formatMoney(cents) {

    const amount =
      Number(cents) / 100;

    /*
     * Use Shopify's native
     * formatMoney function when available.
     */
    if (
      window.Shopify &&
      typeof Shopify.formatMoney ===
        'function'
    ) {
      return Shopify.formatMoney(
        cents,
        window.Shopify.money_format ||
          '${{amount}}'
      );
    }

    /*
     * Fallback formatting
     */
    return new Intl.NumberFormat(
      undefined,
      {
        style: 'currency',

        currency:
          window.Shopify?.currency
            ?.active ||
          'USD'
      }
    ).format(amount);
  }

  /*
   * Escape HTML
   */
  escapeHtml(value) {

    const div =
      document.createElement(
        'div'
      );

    div.textContent =
      value || '';

    return div.innerHTML;
  }
}


/*
 * ==========================================================
 * INITIALIZE CART DRAWER RECOMMENDATIONS
 * ==========================================================
 */

function initCartDrawerRecommendations() {

  const containers =
    document.querySelectorAll(
      '[data-cart-recommendations]'
    );

  containers.forEach(
    (container) => {

      /*
       * Prevent duplicate initialization
       */
      if (
        container.dataset
          .initialized === 'true'
      ) {
        return;
      }

      container.dataset.initialized =
        'true';

      new CartDrawerRecommendations(
        container
      );
    }
  );
}


/*
 * ==========================================================
 * INITIAL PAGE LOAD
 * ==========================================================
 */

document.addEventListener(
  'DOMContentLoaded',
  () => {
    initCartDrawerRecommendations();
  }
);


/*
 * ==========================================================
 * DAWN CART DRAWER OBSERVER
 * ==========================================================
 *
 * Dawn can replace the cart drawer HTML
 * after AJAX cart updates.
 *
 * MutationObserver detects the new
 * recommendation container and initializes
 * it again.
 * ==========================================================
 */

const cartDrawerObserver =
  new MutationObserver(
    () => {
      initCartDrawerRecommendations();
    }
  );


document.addEventListener(
  'DOMContentLoaded',
  () => {

    const cartDrawer =
      document.querySelector(
        'cart-drawer'
      );

    if (!cartDrawer) {
      return;
    }

    cartDrawerObserver.observe(
      cartDrawer,
      {
        childList: true,
        subtree: true
      }
    );
  }
);